import { useState, useEffect } from 'react';
import api from '@/lib/api';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { toast } from 'sonner';
import { FileText, AlertTriangle, Info, RefreshCw } from 'lucide-react';

const LEVEL_BADGES = {
  info: 'bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-300',
  warning: 'bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-300',
  error: 'bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-300',
  success: 'bg-emerald-100 text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-300',
};

export default function SystemLogs() {
  const [logs, setLogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [levelFilter, setLevelFilter] = useState('all');
  const [typeFilter, setTypeFilter] = useState('all');

  useEffect(() => { fetchLogs(); }, [levelFilter, typeFilter]);

  const fetchLogs = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (levelFilter !== 'all') params.set('level', levelFilter);
      if (typeFilter !== 'all') params.set('event_type', typeFilter);
      params.set('limit', '200');
      const res = await api.get(`/admin/system-logs?${params.toString()}`);
      setLogs(res.data);
    } catch { toast.error('Failed to load logs'); }
    finally { setLoading(false); }
  };

  const eventTypes = [...new Set(logs.map(l => l.event_type).filter(Boolean))];

  return (
    <div className="space-y-5 animate-fade-in" data-testid="system-logs-page">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="font-heading text-2xl md:text-3xl font-bold text-foreground tracking-tight">System Logs</h1>
          <p className="text-sm text-muted-foreground mt-0.5">Monitor system events, errors, and activity</p>
        </div>
        <Button variant="outline" size="sm" className="gap-1" onClick={fetchLogs} data-testid="refresh-logs-btn">
          <RefreshCw className="w-3.5 h-3.5" /> Refresh
        </Button>
      </div>

      {/* Filters */}
      <div className="flex gap-3">
        <Select value={levelFilter} onValueChange={setLevelFilter}>
          <SelectTrigger className="w-36 h-9 text-sm" data-testid="level-filter">
            <SelectValue placeholder="Filter by level" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Levels</SelectItem>
            <SelectItem value="info">Info</SelectItem>
            <SelectItem value="warning">Warning</SelectItem>
            <SelectItem value="error">Error</SelectItem>
            <SelectItem value="success">Success</SelectItem>
          </SelectContent>
        </Select>
        <Select value={typeFilter} onValueChange={setTypeFilter}>
          <SelectTrigger className="w-48 h-9 text-sm" data-testid="type-filter">
            <SelectValue placeholder="Filter by type" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Types</SelectItem>
            {eventTypes.map(t => <SelectItem key={t} value={t}>{t.replace(/_/g, ' ')}</SelectItem>)}
          </SelectContent>
        </Select>
        <Badge variant="outline" className="text-xs self-center">{logs.length} logs</Badge>
      </div>

      {/* Logs Table */}
      <Card className="border border-border" data-testid="logs-table-card">
        <CardContent className="p-0">
          <div className="overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead className="text-xs w-20">Level</TableHead>
                  <TableHead className="text-xs w-40">Event</TableHead>
                  <TableHead className="text-xs">Message</TableHead>
                  <TableHead className="text-xs w-40">Timestamp</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {loading ? (
                  <TableRow><TableCell colSpan={4} className="text-center py-8 text-muted-foreground">Loading...</TableCell></TableRow>
                ) : logs.length === 0 ? (
                  <TableRow><TableCell colSpan={4} className="text-center py-8 text-muted-foreground">No logs found</TableCell></TableRow>
                ) : (
                  logs.map(log => (
                    <TableRow key={log.id}>
                      <TableCell>
                        <Badge className={`text-[10px] ${LEVEL_BADGES[log.level] || LEVEL_BADGES.info}`}>
                          {log.level}
                        </Badge>
                      </TableCell>
                      <TableCell className="text-xs font-mono">{log.event_type?.replace(/_/g, ' ')}</TableCell>
                      <TableCell className="text-xs text-muted-foreground max-w-md truncate">{log.message}</TableCell>
                      <TableCell className="text-xs text-muted-foreground font-mono">
                        {log.created_at ? new Date(log.created_at).toLocaleString() : '-'}
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
